/* What the apps on a Wix page are, in plain words. The page checker reads the
   installed-app list out of a Wix page's own data (the `wix-viewer-model`
   script) and uses this file to say which apps are Wix's own, which ones Wix
   adds to every store, and which do a job that other installed apps may also
   do (email, texts, pop-ups, visitor recordings).
   The job labels are ours and deliberately coarse: one job per listed app,
   even where an app does more. Apps whose kind of pop-up wouldn't compete with
   a sign-up pop-up (bars, giveaways, quizzes, sales notices) have no job, so
   they never count as an overlap. A name that is not listed here is shown as
   "Other app" and is never guessed at. The data comes from a survey of 41
   real Wix stores on 2026-09-20 and the App Market listings of that day;
   listings are renamed now and then, so a match by name is a hint, and
   an app's id (which a rename cannot change) wins where we know it.
   Pure data and functions: no DOM, no network. */
(function (root, factory) {
  'use strict';
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.WixAppCatalog = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  const JOBS = Object.freeze({
    email: 'Email marketing',
    sms: 'Text and WhatsApp marketing',
    popups: 'Pop-ups and sign-up forms',
    tracking: 'Visitor recordings and analytics'
  });

  // [listing name on the App Market, short name, job (or null), keyword a renamed listing keeps]
  const LISTED = [
    ['Poptin Pop Ups Email Marketing', 'Poptin', 'popups', 'poptin'],
    ['Omnisend Email Marketing & SMS', 'Omnisend', 'email', 'omnisend'],
    ['PoCo Omnisend Email Marketing', 'Omnisend', 'email', ''],
    ['Klaviyo', 'Klaviyo', 'email', 'klaviyo'],
    ['Klaviyo Forms', 'Klaviyo', 'email', ''],
    ['Klaviyo Email Marketing - PoCo', 'Klaviyo', 'email', ''],
    ['Mailchimp', 'Mailchimp', 'email', 'mailchimp'],
    ['Mailchimp Email Marketing PoCo', 'Mailchimp', 'email', ''],
    ['MailChimp Newsletter', 'Mailchimp', 'email', ''],
    ['Contact Collection', 'Mailchimp', 'email', ''],
    ['Mailchimp Forms by MailMunch', 'Mailmunch', 'popups', 'mailmunch'],
    ['MailMunch Forms', 'Mailmunch', 'popups', ''],
    ['Carti Abandoned Cart Recovery', 'Carti', 'email', 'carti'],
    ['Flashy - Email, SMS & Popups', 'Flashy', 'email', 'flashy'],
    ['OnVoard Email Marketing', 'OnVoard', 'email', 'onvoard'],
    ['SMS Marketing WhatsApp Mercuri', 'Mercuri', 'sms', 'mercuri'],
    ['Constant Contact', 'Constant Contact', 'email', 'constant contact'],
    ['Constant Contact by Digioh', 'Constant Contact', 'email', ''],
    ['MailerLite', 'Mailerlite', 'email', 'mailerlite'],
    ['smoove Email Marketing', 'smoove', 'email', 'smoove'],
    ['Neowauk -Survey, Quiz & Email', 'Neowauk', null, 'neowauk'],
    ['Zotabox Onsite Marketing Tools', 'Zotabox', 'popups', 'zotabox'],
    ['ChilliPopup', 'ChilliPopup', 'popups', 'chillipopup'],
    ['Wix Email Marketing', 'Wix Email Marketing', 'email', ''],
    ['Mailing List', 'Mailing List', 'popups', ''],
    ['Wix Get Subscribers', 'Wix Get Subscribers', 'popups', ''],
    ['Popup Sales Pop, Email & Coupons by Popify', 'Popify', null, 'popify'],
    ['Popup Sales & Email Collection', 'Popup Sales & Email Collection', 'popups', ''],
    ['Coupon X Discount Code Pop Ups', 'Coupon X', 'popups', 'coupon x'],
    ['A/B Testing, Popups & Upsell', 'Personizely', 'popups', 'personizely'],
    ['Popups', 'Smartarget', 'popups', ''],
    ['Email & Newsletter Popup', 'Smartarget', 'popups', ''],
    ['Smartarget Exit Popup', 'Smartarget', 'popups', ''],
    ['Message Bar Smartarget', 'Smartarget', null, ''],
    ['Smartarget Lucky Wheel', 'Smartarget Lucky Wheel', 'popups', ''],
    ['Email, Coupon & sales popup', 'Promolayer', 'popups', 'promolayer'],
    ['Popup', 'Popup', 'popups', ''],
    ['Lumifish Pop-Up', 'Lumifish', 'popups', 'lumifish'],
    ['Welcome Bar', 'Welcome Bar', null, ''],
    ['Impressive Welcome Bar', 'Impressive Welcome Bar', null, ''],
    ['Conversion Popup', 'Conversion Popup', 'popups', ''],
    ['Visitor Hook', 'Visitor Hook', 'popups', 'visitor hook'],
    ['One Click Conversion & Popups', 'Convertify', 'popups', 'convertify'],
    ['ActionPop:100+ Popup Templates', 'ActionPop', 'popups', 'actionpop'],
    ['Exit Intent Pop up', 'Exit Intent Pop up', 'popups', ''],
    ['Unlimited spin to win', 'Unlimited Spin to Win', 'popups', 'unlimited spin'],
    ['Wheelify - Discount Spin Wheel', 'Wheelify', 'popups', 'wheelify'],
    ['Spin Wheel Game', 'Spin Wheel Game', 'popups', ''],
    ['Spin Wheel Email Popup', 'Spin Wheel Email Popup', 'popups', ''],
    ['Popup Games', 'Popup Games', 'popups', ''],
    ['Lucky Wheel', 'Lucky Wheel', 'popups', ''],
    ['Giveaway Ninja', 'Giveaway Ninja', null, 'giveaway ninja'],
    ['Spin-a-Sale', 'Spin-a-Sale', 'popups', 'spin-a-sale'],
    ['Salesdish Lucky Wheel', 'Salesdish Lucky Wheel', 'popups', ''],
    ['WhatsApp Marketing', 'WhatsApp Marketing', 'sms', ''],
    ['SMS Blast', 'SMS Blast', 'sms', 'sms blast'],
    ['Notify Customers SMS Marketing', 'Notify Customers', 'sms', 'notify customers'],
    ['WhatsApp Notifications', 'WhatsApp Notifications', 'sms', ''],
    ['Cart Recovery', 'MarketPush Cart Recovery', 'sms', ''],
    ['SMS Alert', 'SMS Alert', 'sms', ''],
    ['Lume: WhatsApp Cart Recovery', 'Lume', 'sms', 'lume:'],
    ['TWIPLA Website Intelligence', 'TWIPLA', 'tracking', 'twipla'],
    ['Web-Stat Visitor Analytics', 'Web-Stat', 'tracking', 'web-stat'],
    ['Microsoft Clarity', 'Microsoft Clarity', 'tracking', 'clarity'],
    ['Webeyez App', 'Webeyez', 'tracking', 'webeyez'],
    ['Insite AI', 'Insite AI', 'tracking', ''],
    ['Virtari Visitor Analytics', 'Virtari', 'tracking', 'virtari']
  ];

  // Apps whose ids we have seen on real stores, by short name. An id beats a name.
  const KNOWN_IDS = {
    'e6a6244f-597b-46d5-94fe-fcb18ff4a1c4': 'Poptin',
    '13ee53b4-2343-b641-c84d-056d2e6ed2e6': 'TWIPLA',
    'dad178e5-571d-45bf-89a0-c1f97242199f': 'TWIPLA',
    'ab9894d4-6685-4565-91bf-e0c0b849728e': 'Microsoft Clarity',
    '1375baa8-8eca-5659-ce9d-455b2009250d': 'Wix Get Subscribers',
    '8c8727d9-2c8d-401f-b82f-e3e15cbf0864': 'Popup Sales & Email Collection'
  };

  // Added to a Wix store without anyone choosing them (on at least three quarters
  // of the 41 stores surveyed, or the members-area pages a store's checkout adds).
  const STANDARD = {
    '215238eb-22a5-4c36-9e7b-e7c08025e04e': 'Wix Stores',
    '1380b703-ce81-ff05-f115-39571d94dfcd': 'Checkout & Orders',
    '135c3d92-0fea-1f9d-2ba5-2a1dfb04297e': 'ShoutOut (Legacy)',
    '14ce1214-b278-a7e4-1373-00cebd1bef7c': 'Old Wix Forms and Payments',
    'e593b0bd-b783-45b8-97c2-873d42aacaf4': 'Wix CMS',
    'databinding': 'Data Binding',
    '14cc59bc-f0b7-15b8-e1c7-89ce41d0e0c9': 'Wix Members Area',
    'b976560c-3122-4351-878f-453f337b7245': 'Members Area',
    '14cffd81-5215-0a7f-22f8-074b0e2401fb': 'Member Account Info',
    '1505b775-e885-eb1b-b665-1e485d9bf90e': 'My Addresses',
    '4aebd0cb-fbdb-4da7-b5d1-d05660a30172': 'My Wallet',
    '14ce28f7-7eb0-3745-22f8-074b0e2401fb': 'Profile Card',
    '2bef2abe-7abe-43da-889c-53c1500a328c': 'My Subscriptions',
    '14dbef06-cc42-5583-32a7-3abd44da4908': 'Members About',
    '14f25dc5-6af3-5420-9568-f9c5ed98c9b1': 'Members Notifications Settings',
    '14f25924-5664-31b2-9568-f9c5ed98c9b1': 'Wix Members Area Notifications',
    '14dbefd2-01b4-fb61-32a7-3abd44da4908': 'Members',
    '14ebe801-d78a-daa9-c9e5-0286a891e46f': 'Followers'
  };

  const normalize = text =>
    String(text == null ? '' : text)
      .toLowerCase()
      .replace(/[‐-―]/g, '-')
      .replace(/[’]/g, "'")
      .replace(/\s+/g, ' ')
      .trim();

  const byListing = new Map();
  LISTED.forEach((entry, index) => { if (!byListing.has(normalize(entry[0]))) byListing.set(normalize(entry[0]), index); });

  function entryFor(app) {
    const id = normalize(app.id);
    if (id && KNOWN_IDS[id]) {
      const found = LISTED.find(entry => entry[1] === KNOWN_IDS[id]);
      if (found) return found;
    }
    const name = normalize(app.name);
    if (byListing.has(name)) return LISTED[byListing.get(name)];
    return LISTED.find(entry => entry[3] && name.includes(entry[3])) || null;
  }

  // One installed app -> what we can say about it.
  //   kind 'wix-standard': comes with every store (Wix Stores, checkout, members pages)
  //   kind 'wix':          one of Wix's own apps, not on every store
  //   kind 'listed':       a third-party app we know, with its job
  //   kind 'other':        a third-party app we do not know (never guessed at)
  function classifyApp(app) {
    const id = normalize(app.id);
    const name = String(app.name || '').trim();
    if (STANDARD[id]) return { kind: 'wix-standard', name, job: null };
    const entry = entryFor(app);
    if (app.wixOwn) return { kind: 'wix', name, job: entry ? entry[2] : null };
    if (entry) return { kind: 'listed', name, short: entry[1], job: entry[2] };
    return { kind: 'other', name, job: null };
  }

  // The `wix-viewer-model` script (valid JSON as it stands: never HTML-decode
  // it) lists installed apps in three places (checked on 21 real pages, 2026-10-02):
  //   siteFeaturesConfigs.tpaCommons.appsClientSpecMapData   keyed BY app id; the entry has no id of its own
  //   siteFeaturesConfigs.tpaCommons.widgetsClientSpecMapData one entry per placed widget (carries the id)
  //   siteFeaturesConfigs.platform.bootstrapData.appsSpecData platform apps (carries the id)
  // plus isWixTPA on Wix's own. Walk the whole JSON rather than trust one path
  // (Wix has moved it before): an object with appDefinitionName and either an
  // appDefinitionId of its own or a parent key that is the id.
  function appsFromViewerModel(jsonText) {
    let data;
    try { data = JSON.parse(jsonText); } catch (_) { return null; }
    const seen = new Map();
    const stack = [[data, '']];
    while (stack.length) {
      const [node, key] = stack.pop();
      if (Array.isArray(node)) { for (let i = node.length - 1; i >= 0; i--) stack.push([node[i], '']); continue; }
      if (!node || typeof node !== 'object') continue;
      const name = typeof node.appDefinitionName === 'string' ? node.appDefinitionName.trim() : '';
      const own = typeof node.appDefinitionId === 'string' ? node.appDefinitionId.trim() : '';
      const id = own || (name && key ? key : '');
      if (id && name) {
        const known = seen.get(id.toLowerCase());
        if (!known) seen.set(id.toLowerCase(), { id, name, wixOwn: node.isWixTPA === true });
        else if (node.isWixTPA === true) known.wixOwn = true;
      }
      for (const child of Object.keys(node)) if (node[child] && typeof node[child] === 'object') stack.push([node[child], child]);
    }
    return Array.from(seen.values()).sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
  }

  // The site's own Custom Code snippets (Settings > Custom Code) are listed in
  // the same model under siteFeaturesConfigs.codeEmbed.htmlEmbeds: a name, a
  // position (head, bodyStart, bodyEnd), optionally the pages it is limited to,
  // and the code itself. Returns [] when there are none and null for bad JSON.
  function customCodeFromViewerModel(jsonText) {
    let data;
    try { data = JSON.parse(jsonText); } catch (_) { return null; }
    const list = data && data.siteFeaturesConfigs && data.siteFeaturesConfigs.codeEmbed && data.siteFeaturesConfigs.codeEmbed.htmlEmbeds;
    if (!Array.isArray(list)) return [];
    return list
      .filter(item => item && typeof item === 'object')
      .map(item => ({
        name: typeof item.name === 'string' ? item.name.trim() : '',
        position: typeof item.position === 'string' ? item.position : '',
        pages: Array.isArray(item.pages) ? item.pages.length : 0,
        code: item.content && typeof item.content.html === 'string' ? item.content.html : ''
      }));
  }

  // Apps that do the same job. Two of Wix's own apps in one job (the email app
  // and the subscribe form, say) work together, so a group counts only when at
  // least one app in it is a third-party app we know.
  function overlaps(classified) {
    const byJob = {};
    classified.forEach(app => {
      if ((app.kind !== 'listed' && app.kind !== 'wix') || !app.job) return;
      (byJob[app.job] = byJob[app.job] || []).push(app);
    });
    return Object.keys(byJob)
      .filter(job => byJob[job].length > 1 && byJob[job].some(app => app.kind === 'listed'))
      .map(job => ({ job, label: JOBS[job], apps: byJob[job] }));
  }

  return Object.freeze({ JOBS, LISTED, KNOWN_IDS, STANDARD, normalize, classifyApp, appsFromViewerModel, customCodeFromViewerModel, overlaps });
});
